// Duels (contracts/phase4.md §B, "Admin": GET /duels?status): every duel with its mode, both sides
// (orb, rating going in, score, rating change), status and gap. The detail (GET /duels/:id, "Phase 4
// follow-ups") shows each side's handle, orb, rating before → after with the delta and RD, the current
// tier, the set (mean ± spread), points and solving time, the per-puzzle results, timings and flags
// (G6; never the answers), the status history (derived rows marked), the series and the duel's flags.

import type { CSSProperties } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import type { DuelStatusFilter } from "../api/client";
import { useDuel, useDuels } from "../api/queries";
import { DuelStatus, type DuelFlag, type DuelRow, type DuelSeries, type DuelSide } from "../api/schemas";
import { Orb } from "../components/Orb";
import { tableStyles } from "../components/table";
import { Card, Chip, EmptyState, ErrorState, Field, Loading, PageHeader, Select, Skeleton, Stat } from "../components/ui";
import { formatDateTime } from "../lib/dates";
import { DUEL_STATUS_TONE, fmtDelta, scoreLine, TIER_COLOR, tierFor, type RatingTier } from "../lib/duels";
import { fmtInt, fmtMs, shortId, typeSlug } from "../lib/format";
import d from "./duels.module.css";
import styles from "./pages.module.css";

const STATUSES = DuelStatus.options;
const FILTERS: string[] = ["open", "closed", ...STATUSES];
const STATUS_HINT: Record<DuelStatus, string> = {
  queued: "a ladder request waiting for an opponent",
  pending: "sent, not accepted yet",
  choosing: "a series game waiting for its type",
  active: "accepted: the 48 h window is running",
  settled: "both finished or the window closed",
  declined: "the opponent said no",
  expired: "nobody accepted in 48 h",
  cancelled: "withdrawn",
};

/** "26 Sept, 08:29" (UTC; the header says so). */
const short = (iso: string) => formatDateTime(iso).replace(" UTC", "");

const sideName = (s: DuelSide | null) => (s ? (s.handle ? `@${s.handle}` : (s.name ?? (s.userId ? shortId(s.userId) : "—"))) : null);

export function DuelsPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("status");
  const status: DuelStatusFilter = FILTERS.includes(raw ?? "") ? (raw as DuelStatusFilter) : null;
  const q = useDuels(status);
  const navigate = useNavigate();
  const items = q.data?.items ?? [];
  return (
    <>
      <PageHeader title="Duels" meta={<span>Asynchronous 1v1s, newest first: ten puzzles each from calibrated sets, 48 hours from acceptance. Times in UTC.</span>} />
      <div className={styles.filters}>
        <Field label="Status">
          <Select aria-label="Status" value={status ?? ""} onChange={(e) => setParams(e.target.value ? { status: e.target.value } : {}, { replace: true })} style={{ width: 280 }}>
            <option value="">All</option>
            <option value="open">Open: queued, pending, choosing, active</option>
            <option value="closed">Closed: settled, declined, expired, cancelled</option>
            {STATUSES.map((x) => (
              <option key={x} value={x}>
                {`${x[0]!.toUpperCase()}${x.slice(1)}: ${STATUS_HINT[x]}`}
              </option>
            ))}
          </Select>
        </Field>
        <span className={styles.filtersEnd}>
          <span className="monoLabel">{q.data ? `${items.length} duel${items.length === 1 ? "" : "s"}` : ""}</span>
        </span>
      </div>
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className={tableStyles.wrap} aria-busy={q.isFetching}>
          <table className={tableStyles.table} data-testid="duels">
            <thead>
              <tr>
                <th>Created</th>
                <th>Mode</th>
                <th>Challenger</th>
                <th>Opponent</th>
                <th>Status</th>
                <th data-align="right">Score</th>
                <th data-align="right">Rating Δ</th>
                <th data-align="right">Gap</th>
                <th>Ends · settled</th>
              </tr>
            </thead>
            <tbody>
              {q.isPending
                ? Array.from({ length: 10 }, (_, i) => (
                    <tr key={i}>
                      {[90, 60, 120, 120, 60, 40, 60, 30, 90].map((w, j) => (
                        <td key={j}>
                          <Skeleton height={12} width={w} />
                        </td>
                      ))}
                    </tr>
                  ))
                : items.map((x) => <DuelListRow key={x.id} duel={x} onOpen={() => navigate(`/duels/${encodeURIComponent(x.id)}`)} />)}
            </tbody>
          </table>
          {!q.isPending && items.length === 0 ? <EmptyState title={status ? `No ${status} duels` : "No duels yet"}>Duels players start in the app show up here.</EmptyState> : null}
        </div>
      )}
    </>
  );
}

function SideCell({ side, won }: { side: DuelSide | null; won: boolean }) {
  if (!side) return <span className={tableStyles.faint}>in the ladder queue…</span>;
  return (
    <span className={d.side} data-won={won || undefined}>
      {side.orb !== null ? <Orb index={side.orb} size={16} /> : null}
      <span className={d.sideName}>{sideName(side)}</span>
      {side.rating !== null ? <span className={d.rating}>{Math.round(side.rating)}</span> : null}
      {side.forfeit ? <Chip tone="warning">forfeit</Chip> : null}
    </span>
  );
}

function DuelListRow({ duel: x, onOpen }: { duel: DuelRow; onOpen: () => void }) {
  return (
    <tr data-clickable data-duel={x.id} data-status={x.status} onClick={(e) => !(e.target as HTMLElement).closest("a") && onOpen()}>
      <td className={tableStyles.mono}>
        <Link to={`/duels/${encodeURIComponent(x.id)}`} className={tableStyles.id}>
          {x.createdAt ? short(x.createdAt) : shortId(x.id)}
        </Link>
      </td>
      <td>
        <span className={d.mode}>
          <Chip tone={x.mode === "friend" ? "neutral" : "violet"} title={x.rated ? "Rated (Glicko-2)" : "Unrated"}>
            {`${x.mode}${x.game ? ` ${x.game}/3` : ""}${x.rated ? " · rated" : ""}`}
          </Chip>
          {x.type ? <span className={tableStyles.faint}>{x.type}</span> : null}
        </span>
      </td>
      <td>
        <SideCell side={x.challenger} won={x.winner === "challenger"} />
      </td>
      <td>
        <SideCell side={x.opponent} won={x.winner === "opponent"} />
      </td>
      <td>
        <Chip tone={DUEL_STATUS_TONE[x.status]} title={STATUS_HINT[x.status]}>
          {x.status}
        </Chip>
      </td>
      <td data-align="right" className="mono">
        {x.winner === "void" ? <span className={tableStyles.faint}>void</span> : scoreLine(x)}
      </td>
      <td data-align="right" className="mono">
        {x.rated && (x.challenger.ratingDelta !== null || x.opponent?.ratingDelta != null) ? (
          <span className={d.deltas}>
            <Delta v={x.challenger.ratingDelta} /> / <Delta v={x.opponent?.ratingDelta ?? null} />
          </span>
        ) : (
          <span className={tableStyles.faint}>{x.rated ? "—" : "unrated"}</span>
        )}
      </td>
      <td data-align="right" className="mono">
        {x.gap === null ? <span className={tableStyles.faint}>—</span> : x.gap.toFixed(1)}
      </td>
      <td className={tableStyles.mono}>
        {x.status === "settled" && x.settledAt ? (
          <span className={tableStyles.dim} title="Settled">
            ✓ {short(x.settledAt)}
          </span>
        ) : x.windowEndsAt ? (
          <span title="Window ends">{short(x.windowEndsAt)}</span>
        ) : (
          <span className={tableStyles.faint}>—</span>
        )}
      </td>
    </tr>
  );
}

function Delta({ v }: { v: number | null | undefined }) {
  return (
    <span className={d.delta} data-sign={v === null || v === undefined ? undefined : v > 0 ? "up" : v < 0 ? "down" : "zero"}>
      {fmtDelta(v)}
    </span>
  );
}

// ---------------------------------------------------------------- detail

const FLAG_LABEL: Record<string, string> = {
  too_fast: "too fast",
  clock_mismatch: "clock mismatch",
  moderation_hold: "moderation hold",
  forfeit: "forfeit",
  unfinished: "unfinished",
  void: "void: nobody played",
  overdue: "overdue: the window passed, not settled yet",
};

/** One line per kind and side, with the puzzles (1-based) it applies to. */
function groupFlags(flags: DuelFlag[]) {
  const groups = new Map<string, { kind: string; side: DuelFlag["side"]; puzzles: number[] }>();
  for (const f of flags) {
    const key = `${f.kind}|${f.side ?? ""}`;
    const g = groups.get(key) ?? { kind: f.kind, side: f.side, puzzles: [] };
    if (f.index !== null) g.puzzles.push(f.index + 1);
    groups.set(key, g);
  }
  return [...groups.values()].map((g) => ({ ...g, puzzles: [...new Set(g.puzzles)].sort((a, b) => a - b) }));
}

/** Short labels for the per-puzzle table. */
const FLAG_SHORT: Record<string, string> = { too_fast: "fast", clock_mismatch: "clock" };

export function DuelDetailPage() {
  const { id = "" } = useParams();
  const q = useDuel(id);
  const x = q.data?.duel;
  const header = (
    <PageHeader
      title={
        x ? (
          <span className={d.title}>
            {sideName(x.challenger)} <span className={d.vs}>vs</span> {sideName(x.opponent) ?? "…"}
          </span>
        ) : (
          <span className="mono">{shortId(id, 20)}</span>
        )
      }
      meta={
        <>
          <Link to="/duels" className={styles.back}>
            ← Duels
          </Link>
          {x ? (
            <>
              <Chip tone={DUEL_STATUS_TONE[x.status]} title={STATUS_HINT[x.status]}>
                {x.status}
              </Chip>
              <Chip tone={x.mode === "friend" ? "neutral" : "violet"}>{x.mode}</Chip>
              {x.type ? <Chip tone="muted">{`${x.type} set`}</Chip> : null}
              {x.rated ? <Chip tone="accent">rated</Chip> : <Chip tone="muted">unrated</Chip>}
              {x.week ? <span className="mono">{x.week}</span> : null}
              {x.seriesId ? <span className="mono">{`series ${shortId(x.seriesId, 14)}${x.game ? ` · game ${x.game}` : ""}`}</span> : null}
            </>
          ) : null}
          <span className="mono">{id}</span>
        </>
      }
    />
  );
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} title={(q.error as { status?: number }).status === 404 ? "No such duel" : "Couldn't load this duel"} />
      </>
    );
  const { duel, series, history, flags, full } = q.data;
  const flagsFor = (side: "challenger" | "opponent") => flags.filter((f) => f.side === side);
  return (
    <>
      {header}
      <div className={d.sides}>
        <SideCard side={duel.challenger} role="Challenger" duel={duel} won={duel.winner === "challenger"} full={full} flags={flagsFor("challenger")} />
        <div className={d.middle} data-testid="duel-middle">
          <span className={d.vsBig}>VS</span>
          <span className={d.middleScore}>{scoreLine(duel)}</span>
          <span className="monoLabel">{duel.winner === "draw" ? "draw" : duel.winner === "void" ? "void: nobody played" : duel.status === "settled" ? "final" : duel.status}</span>
          {duel.gap !== null ? (
            <span className={d.gap} data-testid="duel-gap">
              Difficulty gap <strong>{duel.gap.toFixed(1)}</strong> of 100
            </span>
          ) : null}
          {duel.link ? (
            <a href={duel.link} target="_blank" rel="noreferrer" className={`${styles.back} mono`}>
              {duel.link.replace(/^https?:\/\//, "")}
            </a>
          ) : null}
        </div>
        {duel.opponent ? (
          <SideCard side={duel.opponent} role="Opponent" duel={duel} won={duel.winner === "opponent"} full={full} flags={flagsFor("opponent")} />
        ) : (
          <Card title="Opponent">
            <EmptyState title="Not matched yet">
              {duel.mode === "ladder" ? "Ladder challenges match the nearest rating queued in the last hour, then anyone active in the last 24 h." : "Waiting for the invited friend."}
            </EmptyState>
          </Card>
        )}
      </div>

      <div className={d.bottom}>
        <div className={d.stack}>
          <Card title="Status history" actions={<span className="monoLabel">UTC</span>}>
            {history.length ? (
              <ol className={styles.timeline} data-testid="duel-history">
                {history.map((h, i) => (
                  <li key={`${h.status}-${i}`} data-done data-derived={h.derived || undefined}>
                    <span className="mono">{short(h.at)}</span>
                    <span>
                      <Chip tone={DUEL_STATUS_TONE[h.status as DuelStatus] ?? "muted"}>{h.status}</Chip>
                    </span>
                    <span className={styles.faint}>
                      {h.from ? `from ${h.from}` : "created"}
                      {h.derived ? " · derived" : ""}
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <ol className={styles.timeline} data-testid="duel-timeline">
                {timeline(duel).map((e) => (
                  <li key={e.label} data-done={e.at ? true : undefined}>
                    <span className="mono">{e.at ? short(e.at) : "—"}</span>
                    <span>{e.label}</span>
                    <span className={styles.faint}>{e.note ?? ""}</span>
                  </li>
                ))}
              </ol>
            )}
            {history.some((h) => h.derived) ? <p className={styles.footNote}>Derived rows were rebuilt from the duel&rsquo;s timestamps (duels older than the status history).</p> : null}
          </Card>
          {full ? (
            <Card title="Flags" actions={<span className="monoLabel">{flags.length ? `${flags.length}` : "none"}</span>}>
              {flags.length ? (
                <ul className={d.flagList} data-testid="duel-flags">
                  {groupFlags(flags).map((g) => (
                    <li key={`${g.kind}-${g.side}`} data-kind={g.kind}>
                      <Chip tone={g.kind === "too_fast" || g.kind === "clock_mismatch" || g.kind === "moderation_hold" ? "warning" : "muted"}>{FLAG_LABEL[g.kind] ?? g.kind}</Chip>
                      <span className={styles.faint}>
                        {g.side ? sideName(g.side === "challenger" ? duel.challenger : duel.opponent) : "the duel"}
                        {g.puzzles.length ? ` · puzzle${g.puzzles.length === 1 ? "" : "s"} ${g.puzzles.join(", ")}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.faint}>No anti-cheat, moderation or settlement flags.</p>
              )}
            </Card>
          ) : null}
          {series ? <SeriesCard series={series} duelId={duel.id} /> : null}
        </div>
        <div className={d.stack}>
          {duel.challenger.puzzles.length || duel.opponent?.puzzles.length ? (
            <>
              <Card title="Per puzzle" actions={<span className="monoLabel">different sets, same calibration</span>}>
                <Breakdown duel={duel} />
              </Card>
              {[duel.challenger, duel.opponent].map((sd, i) =>
                sd && sd.puzzles.length ? (
                  <Card key={i} title={`${sideName(sd)} · set`} padded={false} actions={<span className="monoLabel">{sd.setId ?? ""}</span>}>
                    <div className={d.tableScroll}>
                      <PuzzleTable side={sd} role={i === 0 ? "challenger" : "opponent"} />
                    </div>
                  </Card>
                ) : null,
              )}
            </>
          ) : full ? (
            <Card title="Per puzzle">
              <EmptyState title="No sets yet">The sets are dealt when the duel is accepted.</EmptyState>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}

/** Before the status history existed (or from the list fallback): the duel's events in time order. */
function timeline(duel: DuelRow): { label: string; at: string | null; note?: string }[] {
  const winnerName =
    duel.winner === "draw" || duel.winner === "void" ? duel.winner : duel.winner ? `${sideName(duel.winner === "challenger" ? duel.challenger : duel.opponent)} won` : undefined;
  const events = [
    { label: "Created", at: duel.createdAt },
    { label: "Accepted", at: duel.acceptedAt, note: duel.acceptedAt ? "the 48 h window starts" : undefined },
    { label: `${sideName(duel.challenger)} finished`, at: duel.challenger.finishedAt },
    ...(duel.opponent ? [{ label: `${sideName(duel.opponent)} finished`, at: duel.opponent.finishedAt }] : []),
    { label: "Window ends", at: duel.windowEndsAt },
    { label: "Settled", at: duel.settledAt, note: duel.status === "settled" ? winnerName : undefined },
  ];
  // Steps without a time are left out, except acceptance (a pending duel shows it hasn't happened).
  return events
    .filter((e) => e.at !== null || e.label === "Accepted")
    .sort((x, y) => (x.at === null ? 1 : 0) - (y.at === null ? 1 : 0) || (x.at ?? "").localeCompare(y.at ?? ""));
}

function SideCard({ side, role, duel, won, full, flags }: { side: DuelSide; role: string; duel: DuelRow; won: boolean; full: boolean; flags: DuelFlag[] }) {
  // The current tier (backend/api sends it with ratingNow); an older API: derived from the rating.
  const now = side.ratingNow ?? side.ratingAfter ?? side.rating;
  const tier = side.tier ? { tier: side.tier as RatingTier, division: side.division ?? "" } : now !== null ? tierFor(now) : null;
  // backend/api's duel rows (the list fallback) carry each side's score and rating change only.
  const partial = !full && side.setId === null && side.played === null && side.startedAt === null && side.puzzles.length === 0;
  const flagged = flags.filter((f) => f.kind === "too_fast" || f.kind === "clock_mismatch").length;
  return (
    <Card
      title={role}
      className={d.sideCard}
      actions={
        <span className={styles.chips}>
          {won ? <Chip tone="accent">won</Chip> : null}
          {side.forfeit ? <Chip tone="warning">forfeit</Chip> : null}
          {side.moderationHold ? <Chip tone="danger">held</Chip> : null}
          {flagged ? <Chip tone="warning">{`⚑ ${flagged}`}</Chip> : null}
        </span>
      }
    >
      <div className={d.who} data-testid={`duel-${role.toLowerCase()}`}>
        {side.orb !== null ? <Orb index={side.orb} size={44} label={`Orb ${side.orb}`} /> : null}
        <div className={d.whoText}>
          {side.userId ? (
            <Link to={`/players/${side.userId}`} className={d.whoName}>
              {sideName(side)}
            </Link>
          ) : (
            <span className={d.whoName}>{sideName(side)}</span>
          )}
          {tier ? (
            <span className={d.tier} style={{ "--tier": TIER_COLOR[tier.tier] ?? "var(--color-text-quaternary)" } as CSSProperties} title={now !== null ? `Now ${Math.round(now)}` : undefined}>
              <i />
              {tier.tier} {tier.division}
            </span>
          ) : null}
        </div>
        <span className={d.bigScore} data-won={won || undefined}>
          {side.forfeit ? "F" : side.score === null ? "—" : fmtInt(side.score)}
        </span>
      </div>
      {partial ? (
        <>
          <div className={styles.statsRow}>
            <Stat label="Change" value={duel.rated ? <Delta v={side.ratingDelta} /> : <span className={styles.faint}>unrated</span>} />
            <Stat label="Set score" value={side.forfeit ? "forfeit" : side.score === null ? "—" : fmtInt(side.score)} sub="of 100" />
          </div>
          <p className={`${styles.footNote} ${d.partialNote}`} data-testid="duel-partial">
            The admin API lists this duel's scores and rating changes only; its sets, ratings and timings aren't served yet.
          </p>
        </>
      ) : (
        <>
          <div className={styles.statsRow}>
            <Stat
              label="Rating"
              value={side.rating === null ? "—" : Math.round(side.rating)}
              sub={side.rdBefore !== null ? `RD ${Math.round(side.rdBefore)}${side.rdAfter !== null ? ` → ${Math.round(side.rdAfter)}` : ""}` : side.rd !== null ? `RD ${Math.round(side.rd)}` : "going in"}
            />
            <Stat
              label="Change"
              value={duel.rated ? <Delta v={side.ratingDelta} /> : <span className={styles.faint}>unrated</span>}
              sub={side.ratingAfter !== null && duel.rated ? `→ ${Math.round(side.ratingAfter)}` : undefined}
            />
            <Stat label="Played" value={side.played === null ? "—" : `${side.played}/10`} sub={side.finished ? "finished" : side.startedAt ? "in progress" : "not started"} />
            <Stat
              label="Points"
              value={side.points === null ? "—" : fmtInt(side.points)}
              sub={side.timeMs ? `${fmtMs(side.timeMs)} solving` : "of 10 000"}
            />
          </div>
          <dl className={`${styles.kv} ${d.sideKv}`}>
            <dt>Set</dt>
            <dd className="mono">
              {side.setId ?? "—"}
              {side.setMean !== null ? (
                <span className={styles.faint}>
                  {" "}
                  · mean {side.setMean.toFixed(1)}
                  {side.setSpread !== null ? ` ± ${side.setSpread.toFixed(1)}` : ""}
                </span>
              ) : null}
            </dd>
            <dt>Started</dt>
            <dd className="mono">{side.startedAt ? formatDateTime(side.startedAt) : "—"}</dd>
            <dt>Finished</dt>
            <dd className="mono">
              {side.finishedAt ? formatDateTime(side.finishedAt) : "—"}
              {side.startedAt && side.finishedAt ? <span className={styles.faint}> · {fmtMs(Date.parse(side.finishedAt) - Date.parse(side.startedAt))} elapsed</span> : null}
            </dd>
          </dl>
        </>
      )}
    </Card>
  );
}

/** G6: per-puzzle points for both sets, side by side (the sets differ; only the calibration matches). */
function Breakdown({ duel }: { duel: DuelRow }) {
  const sides = [duel.challenger, duel.opponent].filter((s): s is DuelSide => Boolean(s));
  // Points are 0–1000 in backend/api; an older API's 0–10 scale is scaled up.
  const max = Math.max(10, ...sides.flatMap((s) => s.puzzles.map((p) => p.points ?? 0))) > 10 ? 1000 : 10;
  return (
    <div className={d.breakdown} data-testid="duel-breakdown">
      {sides.map((s) => (
        <div key={s.userId ?? s.name} className={d.breakRow}>
          <span className={d.breakName}>{sideName(s)}</span>
          <span className={d.bars} role="img" aria-label={`${sideName(s)}: ${s.puzzles.filter((p) => p.correct).length} of ${s.puzzles.length} right`}>
            {s.puzzles.map((p) => (
              <span
                key={p.n}
                className={d.bar}
                data-state={p.correct === null ? "unplayed" : p.correct ? "right" : "wrong"}
                data-flagged={p.flags.length ? true : undefined}
                style={{ height: `${p.correct ? 30 + ((p.points ?? max) / max) * 70 : 12}%` }}
                title={`${p.n}. ${p.type ?? ""} · ${p.correct === null ? "not played" : p.correct ? `${p.points ?? "?"} pts` : "wrong"}${p.elapsedMs ? ` · ${fmtMs(p.elapsedMs)}` : ""}${p.flags.length ? ` · ⚑ ${p.flags.join(", ")}` : ""}`}
              />
            ))}
          </span>
          <span className="monoLabel">
            {s.puzzles.filter((p) => p.correct).length} of {s.puzzles.length} right
          </span>
        </div>
      ))}
    </div>
  );
}

/** One side's set in order: type, difficulty, result, points, server and client time, flags. Never the answer. */
function PuzzleTable({ side, role }: { side: DuelSide; role: string }) {
  return (
    <table className={`${tableStyles.table} ${d.puzzleTable}`} data-testid={`duel-puzzles-${role}`}>
      <thead>
        <tr>
          <th data-align="right">#</th>
          <th>Type</th>
          <th data-align="right">Diff.</th>
          <th>Result</th>
          <th data-align="right">Points</th>
          <th data-align="right" title="Server-measured · the client's own clock">
            Time · client
          </th>
          <th>Flags</th>
        </tr>
      </thead>
      <tbody>
        {side.puzzles.map((p) => (
          <tr key={p.n} data-flagged={p.flags.length ? true : undefined}>
            <td data-align="right" className="mono">
              {p.n}
            </td>
            <td className={tableStyles.mono} title={[p.type, p.puzzleId].filter(Boolean).join(" · ") || undefined}>
              {p.type ? typeSlug(p.type) : (p.category ?? "—")}
            </td>
            <td data-align="right" className="mono">
              {p.difficulty === null ? "—" : p.difficulty.toFixed(2)}
            </td>
            <td>
              {p.correct === null ? (
                <span className={tableStyles.faint}>{p.started ? "open" : "—"}</span>
              ) : p.correct ? (
                <Chip tone="success">right</Chip>
              ) : (
                <Chip tone="danger">wrong</Chip>
              )}
            </td>
            <td data-align="right" className="mono">
              {p.points === null ? <span className={tableStyles.faint}>—</span> : fmtInt(p.points)}
            </td>
            <td data-align="right" className="mono">
              {p.elapsedMs === null ? <span className={tableStyles.faint}>—</span> : fmtMs(p.elapsedMs)}
              {p.clientElapsedMs !== null ? <span className={tableStyles.faint}> · {fmtMs(p.clientElapsedMs)}</span> : null}
            </td>
            <td>
              {p.flags.length ? (
                <Chip tone="warning" title={p.flags.map((f) => FLAG_LABEL[f] ?? f).join(", ")}>
                  {`⚑ ${p.flags.map((f) => FLAG_SHORT[f] ?? f).join("·")}`}
                </Chip>
              ) : (
                <span className={tableStyles.faint}>—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SeriesCard({ series, duelId }: { series: DuelSeries; duelId: string }) {
  return (
    <Card title="Series" actions={<Chip tone={series.status === "settled" ? "success" : "info"}>{series.status}</Chip>}>
      <div className={styles.statsRow}>
        <Stat label="Wins" value={`${series.wins.challenger}–${series.wins.opponent}`} sub={series.draws ? `${series.draws} drawn` : "challenger–opponent"} />
        <Stat label="Week" value={<span className="mono">{series.week ?? "—"}</span>} sub={series.deadline ? `ends ${short(series.deadline)}` : undefined} />
      </div>
      <ol className={styles.timeline} data-testid="duel-series">
        {series.games.map((g) => (
          <li key={g.game} data-done={g.status === "settled" || undefined}>
            <span className="mono">game {g.game}</span>
            <span>
              {g.duelId === duelId ? (
                <strong>this duel</strong>
              ) : (
                <Link to={`/duels/${encodeURIComponent(g.duelId)}`} className={tableStyles.id}>
                  {shortId(g.duelId, 12)}
                </Link>
              )}{" "}
              <Chip tone={DUEL_STATUS_TONE[g.status as DuelStatus] ?? "muted"}>{g.status}</Chip>
            </span>
            <span className={styles.faint}>
              {g.type ?? ""}
              {g.outcome ? ` · ${g.outcome}` : g.chooser ? ` · ${g.chooser} picks` : ""}
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}
